import React, { useEffect, useRef, useState } from 'react';
import styles from './Parallax.module.scss';
import CircularMenu, { MenuItem } from '../CircularMenu/CircularMenu.tsx';

type ParallaxDirection = 'x' | 'y' | 'xy' | '-xy';

interface LayerConfig {
    src: string;
    mouseSpeed: number;
    scrollSpeed: number;
    zIndex: number;
    direction?: ParallaxDirection; // Направление параллакса при скролле
}

const layerConfigs: LayerConfig[] = [
    { src: '/images/parallax/l0.jpg', mouseSpeed: 10 * 2, scrollSpeed: -0.03, zIndex: 1, direction: 'xy' },
    { src: '/images/parallax/l1.png', mouseSpeed: 15 * 2, scrollSpeed: -0.06, zIndex: 2, direction: '-xy' },
    { src: '/images/parallax/l2.png', mouseSpeed: 40 * 2, scrollSpeed: -0.08, zIndex: 3, direction: 'xy' },
    { src: '/images/parallax/l3.png', mouseSpeed: 60 * 2, scrollSpeed: -0.09, zIndex: 6, direction: '-xy' },
    { src: '', mouseSpeed: 70 * 2, scrollSpeed: -0.05, zIndex: 7, direction: 'y' },
];

const navItems: MenuItem[] = [
    { title: 'Инфраструктура', href: `${window.location.origin}/#Infrasctructure` },
    { title: 'Услуги', href: `${window.location.origin}/#Services` },
    { title: 'Отзывы', href: `${window.location.origin}/#Reveiws` },
    { title: 'Контакты', href: `${window.location.origin}/#Contacts` },
    { title: 'Бронирование', href: `${window.location.origin}/#Aparts` },
];

interface ParallaxProps {
    device: number;
}

const Parallax: React.FC<ParallaxProps> = ({ device }) => {
    const layerRefs = useRef<(HTMLImageElement | HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isOpenMenu, setIsOpenMenu] = useState(false);

    const isMobile = device <= 2;

    // 1. Предзагрузка изображений
    useEffect(() => {
        const imagePromises = layerConfigs.map((config) => {
            return new Promise((resolve) => {
                if (!config.src) {
                    resolve(true);
                    return;
                }
                const img = new Image();
                img.src = config.src;
                img.onload = resolve;
                img.onerror = resolve;
            });
        });

        Promise.all(imagePromises).then(() => {
            setIsLoaded(true);
        });
    }, []);

    // 2. Логика параллакса
    useEffect(() => {
        let animationFrameId: number;

        // Координаты мыши
        let targetX = 0;
        let targetY = 0;
        let currentX = 0;
        let currentY = 0;

        // Координаты скролла
        let targetScrollY = 0;
        let currentScrollY = 0;

        const lerp = (start: number, end: number, factor: number) => {
            return start + (end - start) * factor;
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (isMobile || !containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            targetX = (e.clientX - centerX) / window.innerWidth;
            targetY = (e.clientY - centerY) / window.innerHeight;
        };

        const handleMouseLeave = () => {
            if (isMobile) return;
            targetX = 0;
            targetY = 0;
        };

        const handleScroll = () => {
            if (!isMobile || !containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            targetScrollY = rect.top;
        };

        const container = containerRef.current;

        if (isMobile) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();
        } else {
            if (container) {
                container.addEventListener('mousemove', handleMouseMove);
                container.addEventListener('mouseleave', handleMouseLeave);
            }
        }

        const animate = () => {
            const EPSILON = 0.0001;

            if (isMobile) {
                const deltaScroll = Math.abs(targetScrollY - currentScrollY);

                if (deltaScroll > 0.1) {
                    currentScrollY = lerp(currentScrollY, targetScrollY, 0.1);
                    currentScrollY = Math.max(-900, currentScrollY)

                    layerRefs.current.forEach((layer, index) => {
                        if (!layer) return;
                        const config = layerConfigs[index];
                        const dir = config.direction || 'y';

                        const offset = currentScrollY * config.scrollSpeed;

                        let moveX = 0;
                        let moveY = 0;

                        // Расчет векторов смещения в зависимости от направления
                        switch (dir) {
                            case 'x':
                                moveX = offset;
                                moveY = 0;
                                break;
                            case 'xy':
                                moveX = offset;
                                moveY = offset;
                                break;
                            case '-xy':
                                moveX = -offset;
                                moveY = offset;
                                break;
                            case 'y':
                            default:
                                moveX = 0;
                                moveY = offset;
                                break;
                        }

                        layer.style.transform = `translate3d(${moveX}px, ${moveY}px, 0)`;
                    });
                }
            } else {
                const deltaX = Math.abs(targetX - currentX);
                const deltaY = Math.abs(targetY - currentY);

                if (deltaX > EPSILON || deltaY > EPSILON) {
                    currentX = lerp(currentX, targetX, 0.08);
                    currentY = lerp(currentY, targetY, 0.08);

                    layerRefs.current.forEach((layer, index) => {
                        if (!layer) return;
                        const config = layerConfigs[index];

                        const moveX = currentX * config.mouseSpeed;
                        const moveY = currentY * config.mouseSpeed;

                        layer.style.transform = `translate3d(${moveX}px, ${moveY}px, 0)`;
                    });
                }
            }

            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            window.removeEventListener('scroll', handleScroll);

            if (container) {
                container.removeEventListener('mousemove', handleMouseMove);
                container.removeEventListener('mouseleave', handleMouseLeave);
            }
            cancelAnimationFrame(animationFrameId);
        };
    }, [isMobile]);

    return (
        <div
            className={`${styles.parallaxContainer} ${isLoaded ? styles.loaded : ''}`}
            ref={containerRef}
        >
            {layerConfigs.map((config, index) => (
                config.src !== "" ?
                    <img
                        key={index}
                        ref={(el) => { layerRefs.current[index] = el; }}
                        src={config.src}
                        alt={`Layer ${index}`}
                        className={styles.parallaxLayer}
                        style={{ zIndex: config.zIndex }}
                    /> :
                    <div
                        key={index}
                        ref={(el) => { layerRefs.current[index] = el; }}
                        className={`${styles.parallaxLayer} ${styles.Text}`}
                        style={{ zIndex: config.zIndex }}>
                        <div className={styles.left}>
                            <button className={`${styles.menu} ${isOpenMenu ? styles.opened : ""}`} onClick={() => {
                                setIsOpenMenu(!isOpenMenu)
                            }}></button>
                            {isOpenMenu && (
                                <CircularMenu
                                    items={navItems}
                                    onClose={() => setIsOpenMenu(false)}
                                    radius={device < 4 ? 120 : 150}
                                    device={device}
                                    ANGLES={[-90, -35, 0, 35, 90]}
                                />
                            )}
                        </div>
                        <div className={styles.center}>
                            <img className={styles.logoTransparent} src='/images/logo_white.svg' alt="Logo" />
                            <span className={`${styles.Big} ${styles.Bold}`} >
                                COZY APARTS
                            </span>
                            <span className={`${styles.mainText} ${styles.default} ${styles.Thin} `}>
                                Посуточные апартаменты <br />в Москва Сити
                            </span>
                        </div>
                        <div className={styles.right}>
                            <a className={styles.number} href="tel:+79299023710">+7 929 902 37 10</a>
                            <a className={styles.tg} href="https://t.me/cozzyaparts" target="_blank" rel="noopener noreferrer">
                                <img src="/images/tg_white.svg" alt="Telegram" />
                            </a>
                            <button className={styles.book}><a href={`${window.location.origin}/#Aparts`}>забронировать</a></button>
                        </div>
                    </div>
            ))}
        </div>
    );
};

export default Parallax;